import { CreateDashboardDefinitionBase } from 'src/app/dashboards/shared/analyses/_shared/dashboard-definition.model';
import { axesView, composeHeading, createRouteMethodologyFilter, defineDashboard, isKEur, resolveProduct } from '../../../_shared/dashboard-axes';
import { createTimeFilters, createPeriodLabelTransformers, createPeriodYearTransformers, resolveTimeFilter, timeFilterDetail } from '../../../_shared/time-filters';
import {
  ChartFieldDashboardDataTransformer,
  NumDEDashboardDataTransformer,
  PercentageValueDataTransformer,
  RankDashboardDataTransformer,
  RankingBarValuePctDisplayDashboardDataTransformer,
  RankingValuePctScopeDashboardDataTransformer,
  SumSubMarketNameDataTransformer,
  UndefinedStateToSumGermanyDashboardDataTransformer,
} from '../../../../data_transformers/dashboard_data_transformer';
import { DashboardRankingDefinition } from './dashboard-ranking.definition';
import {
  createIdNameTransformers,
  granularityHeadingPhrase,
  granularityHeadingPhraseByFirst,
  groupDetailOf,
  mapHeadingPhrase,
  mapLabelKeyOf,
} from '../../../../dashboard-config/granularities/granularity-profile';
import { kpiColumn } from '../../../../dashboard-config/kpis/kpi-columns';
import {
  KpiColumnDataTransformer,
  KpiExportDataTransformer,
  KpiLabelDataTransformer,
  KpiValueDisplayDataTransformer,
} from '../../../../dashboard-config/kpis/kpi-data-transformers';
import { createKpiFilters } from '../../../../dashboard-config/kpis/kpi-filters';
import {
  RANKING_VALUE_KEY,
  RANKING_VALUE_SHARE_PCT_KEY,
  rankingChartHeadingLead,
  rankingChartMeasures,
  rankingMapHeadingLead,
  RankingMetricProfile,
  rankingShareMeasure,
} from './dashboard-ranking-metric.profile';
import {
  DashboardRankingShell,
  dashboardRankingShellProfiles,
} from './dashboard-ranking-shell.profile';

export type CreateDashboardRankingDefinitionInput =
  CreateDashboardDefinitionBase<DashboardRankingShell, RankingMetricProfile>;

export function createDashboardRankingDefinition(
  input: CreateDashboardRankingDefinitionInput,
): DashboardRankingDefinition {
  const shellKey = input.shell;
  const { axes, shell, metric } = resolveProduct(input, dashboardRankingShellProfiles);
  const granularityId = axes.granularity.id;
  const mapGranularityId = axes.granularity.mapGranularity;
  const context = axes.kpiContext;
  const mapContext = { ...context, granularity: mapGranularityId };

  /* ---- Time ---- */
  const time = resolveTimeFilter(axes, 'single');
  const { series, timeline } = timeFilterDetail(time);

  /* ---- Headings ---- */
  const chartHeadingLead = rankingChartHeadingLead(metric);
  const mapHeadingLead = rankingMapHeadingLead(metric);
  const headingPhrase = shell.headingByPhraseFirst ? granularityHeadingPhraseByFirst : granularityHeadingPhrase;

  /* ---- View ---- */
  const baseValueColWidth = metric.narrowDistrictValueCol && shellKey === 'districts' ? 80 : shell.baseValueColWidth;

  return defineDashboard<DashboardRankingDefinition>(axes, shell, {
    heading: (filterValues) =>
      composeHeading(
        [chartHeadingLead(filterValues), headingPhrase(granularityId, filterValues)],
        filterValues.get(timeline.filterName),
      ),
    mapHeading: (filterValues) =>
      composeHeading([mapHeadingLead(filterValues), mapHeadingPhrase(granularityId, filterValues)]),

    filters: (deps) => [
      ...createTimeFilters(deps, time),
      ...shell.buildGranularityFilters(deps, { defaultState: axes.selections?.states?.[0] }),
      ...createKpiFilters(axes.metric, granularityId),
      createRouteMethodologyFilter(deps),
    ],

    preFilterTransformers: (deps) => [
      new UndefinedStateToSumGermanyDashboardDataTransformer(),
      ...createPeriodLabelTransformers(deps, axes),
    ],

    // Order matches legacy single-column ranking.
    postFilterTransformers: (deps) => [
      ...rankingChartMeasures(metric, granularityId).map(
        ({ measure, resultColumn }) => new KpiColumnDataTransformer({ measure, resultColumn, context }),
      ),
      new KpiValueDisplayDataTransformer({
        valueColumnKey: RANKING_VALUE_KEY,
        resultColumnKey: RANKING_VALUE_KEY + '_display',
        countUnit: axes.metric.labels.displayUnit ?? '',
      }),
      new SumSubMarketNameDataTransformer(deps.localDatasetsService),
      new RankingBarValuePctDisplayDashboardDataTransformer({
        granularityId,
        valueColumnKey: RANKING_VALUE_SHARE_PCT_KEY,
        resultColumnKey: RANKING_VALUE_SHARE_PCT_KEY + '_display',
      }),
      new RankingValuePctScopeDashboardDataTransformer({ granularityId, resultColumnKey: 'sharePctScope' }),
      ...(metric.extraRanks ?? []).map(
        ({ rankColumn, valueColumn }) => new RankDashboardDataTransformer(rankColumn, valueColumn),
      ),
      // Rank of every KPI, then of the selected one.
      ...axes.metric.kpis().map(
        (kpi) => new RankDashboardDataTransformer(kpiColumn(kpi, 'rank', context), kpiColumn(kpi, 'value', context)),
      ),
      new RankDashboardDataTransformer('rank', RANKING_VALUE_KEY),
      ...createPeriodYearTransformers(axes),
      new KpiLabelDataTransformer(),
      ...createIdNameTransformers(deps, granularityId),
    ],

    exportTransformers: () => [new KpiExportDataTransformer({ kpis: axes.metric.kpis(), percentageMeasures: [] })],

    // Legacy single-column order: rank → share scope → share of the map's parent → display → value_label.
    mapTransformers: () => [
      new RankDashboardDataTransformer('rank', RANKING_VALUE_KEY, { groupColumn: 'year', groupRankColumn: 'rank' }),
      new RankingValuePctScopeDashboardDataTransformer({ granularityId: mapGranularityId, resultColumnKey: 'sharePctScope' }),
      new KpiColumnDataTransformer({
        measure: rankingShareMeasure(metric, mapGranularityId),
        resultColumn: RANKING_VALUE_SHARE_PCT_KEY,
        context: mapContext,
      }),
      new NumDEDashboardDataTransformer({ valueColumnKey: RANKING_VALUE_KEY, resultColumnKey: RANKING_VALUE_KEY + '_display' }),
      new PercentageValueDataTransformer(RANKING_VALUE_SHARE_PCT_KEY, RANKING_VALUE_SHARE_PCT_KEY + '_display'),
      new ChartFieldDashboardDataTransformer('value_label', (row) => row[mapLabelKeyOf(granularityId)]),
    ],

    view: {
      valueKey: RANKING_VALUE_KEY,
      valueSharePctKey: RANKING_VALUE_SHARE_PCT_KEY,
      shellKey,
      ...axesView(axes),
      mapGranularityId,
      groupDetail: groupDetailOf(granularityId, 'name'),
      series,
      timeline,
      chartSyncable: shell.chartSyncable,
      chartHasSlider: shell.chartHasSlider,
      mapValueLabelKey: mapLabelKeyOf(granularityId),
      nameColWidth: shell.nameColWidth,
      baseValueColWidth,
      resolveValueColWidth: (methodologyId) => shell.resolveValueColWidth(methodologyId, baseValueColWidth),
      popup: {
        metricId: axes.metric.id,
        kEur: isKEur(axes),
        slot: metric.popupSlot,
      },
    },
  });
}
